// ─── ScrollEntrance ───────────────────────────────────────────
// Wraps an element and triggers a CSS entrance animation when
// it scrolls into the viewport. Used on public invite pages only.
// Supports: fade, slide-up/down/left/right, zoom, typewriter, word-reveal.

import React, { useRef, useState, useEffect } from 'react';

const ANIMATIONS = {
  none: {},
  fade: {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    transition: 'opacity 0.8s cubic-bezier(0.22,1,0.36,1)',
  },
  'slide-up': {
    hidden: { opacity: 0, transform: 'translateY(40px)' },
    visible: { opacity: 1, transform: 'translateY(0)' },
    transition: 'opacity 0.7s cubic-bezier(0.22,1,0.36,1), transform 0.7s cubic-bezier(0.22,1,0.36,1)',
  },
  'slide-down': {
    hidden: { opacity: 0, transform: 'translateY(-40px)' },
    visible: { opacity: 1, transform: 'translateY(0)' },
    transition: 'opacity 0.7s cubic-bezier(0.22,1,0.36,1), transform 0.7s cubic-bezier(0.22,1,0.36,1)',
  },
  'slide-left': {
    hidden: { opacity: 0, transform: 'translateX(40px)' },
    visible: { opacity: 1, transform: 'translateX(0)' },
    transition: 'opacity 0.7s cubic-bezier(0.22,1,0.36,1), transform 0.7s cubic-bezier(0.22,1,0.36,1)',
  },
  'slide-right': {
    hidden: { opacity: 0, transform: 'translateX(-40px)' },
    visible: { opacity: 1, transform: 'translateX(0)' },
    transition: 'opacity 0.7s cubic-bezier(0.22,1,0.36,1), transform 0.7s cubic-bezier(0.22,1,0.36,1)',
  },
  zoom: {
    hidden: { opacity: 0, transform: 'scale(0.85)' },
    visible: { opacity: 1, transform: 'scale(1)' },
    transition: 'opacity 0.7s cubic-bezier(0.22,1,0.36,1), transform 0.7s cubic-bezier(0.22,1,0.36,1)',
  },

  // ─── Motion 1.0 — Soft Reveal presets ──────────────────────
  // Role-tuned, editorial, quiet. Triggered when the global
  // motionPreset === 'soft_reveal' and the element has no explicit
  // `entrance` set. Cubic-bezier matches the rest of the file.
  'soft-text': {
    hidden:  { opacity: 0, transform: 'translateY(12px)' },
    visible: { opacity: 1, transform: 'translateY(0)' },
    transition: 'opacity 0.75s cubic-bezier(0.22,1,0.36,1), transform 0.75s cubic-bezier(0.22,1,0.36,1)',
  },
  'soft-paper': {
    hidden:  { opacity: 0, transform: 'translateY(16px) scale(0.985)' },
    visible: { opacity: 1, transform: 'translateY(0) scale(1)' },
    transition: 'opacity 0.85s cubic-bezier(0.22,1,0.36,1), transform 0.85s cubic-bezier(0.22,1,0.36,1)',
  },
  'soft-photo': {
    hidden:  { opacity: 0, transform: 'translateY(8px) scale(0.99)' },
    visible: { opacity: 1, transform: 'translateY(0) scale(1)' },
    transition: 'opacity 0.8s cubic-bezier(0.22,1,0.36,1), transform 0.8s cubic-bezier(0.22,1,0.36,1)',
  },
  'soft-decorative': {
    hidden:  { opacity: 0, transform: 'translateY(8px)' },
    visible: { opacity: 1, transform: 'translateY(0)' },
    transition: 'opacity 0.9s cubic-bezier(0.22,1,0.36,1), transform 0.9s cubic-bezier(0.22,1,0.36,1)',
  },
  'soft-bg': {
    hidden:  { opacity: 0 },
    visible: { opacity: 1 },
    transition: 'opacity 1.0s cubic-bezier(0.22,1,0.36,1)',
  },

  // Typewriter and word-reveal are handled by TextReveal component
  typewriter: { isTextEffect: true },
  'word-reveal': { isTextEffect: true },
};

export default function ScrollEntrance({ entrance, delay, children }) {
  const type = entrance || 'none';
  const anim = ANIMATIONS[type];
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  // Phase 1.6 — clear `will-change` after the entrance settles so we don't
  // keep every scroll-revealed element on its own GPU layer forever (causes
  // scroll jank on iOS as the page grows).
  const [animating, setAnimating] = useState(false);

  // Motion 1.0 — honor reduced-motion globally. We check via the same media
  // query Framer Motion's useReducedMotion uses, then bypass the animation
  // entirely. Matches AnimatedSection behavior.
  const [reducedMotion, setReducedMotion] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mq.matches);
    const handler = (e) => setReducedMotion(e.matches);
    mq.addEventListener?.('change', handler);
    return () => mq.removeEventListener?.('change', handler);
  }, []);

  useEffect(() => {
    if (!anim || type === 'none') { setVisible(true); return; }
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          setAnimating(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [type, anim]);

  // Drop `will-change` once the transition completes (slight buffer so we
  // don't drop it mid-frame).
  useEffect(() => {
    if (!animating) return;
    const t = setTimeout(() => setAnimating(false), 1200);
    return () => clearTimeout(t);
  }, [animating]);

  if (type === 'none' || !anim) return children;
  // Motion 1.0 — reduced-motion: render children without any animation.
  if (reducedMotion) return children;

  // Text effects: typewriter & word-reveal
  if (anim.isTextEffect) {
    return (
      <div ref={ref}>
        <TextReveal mode={type} visible={visible} delay={delay}>
          {children}
        </TextReveal>
      </div>
    );
  }

  const delayMs = (delay || 0) * 1000;

  return (
    <div
      ref={ref}
      style={{
        ...(visible ? anim.visible : anim.hidden),
        transition: visible ? anim.transition : 'none',
        transitionDelay: visible ? `${delayMs}ms` : '0ms',
        // Only hint the compositor while the entrance is in flight. Leaving
        // will-change set forever would pin every revealed element to its own
        // GPU layer, causing scroll jank as the page grows. (Phase 1.6)
        willChange: animating ? 'opacity, transform' : 'auto',
      }}
    >
      {children}
    </div>
  );
}

// ─── TextReveal ───────────────────────────────────────────────
// Extracts text from children DOM, splits into chars or words,
// and applies staggered fade-in animations.
function TextReveal({ mode, visible, delay, children }) {
  const containerRef = useRef(null);
  const [text, setText] = useState('');
  const [revealed, setRevealed] = useState(false);

  // Extract text content from rendered children
  useEffect(() => {
    if (!containerRef.current) return;
    const raw = containerRef.current.innerText || '';
    setText(raw);
  }, [children]);

  // Trigger reveal after becoming visible
  useEffect(() => {
    if (!visible || revealed) return;
    const ms = (delay || 0) * 1000;
    const timer = setTimeout(() => setRevealed(true), ms);
    return () => clearTimeout(timer);
  }, [visible, delay, revealed]);

  const isTypewriter = mode === 'typewriter';
  const units = isTypewriter ? text.split('') : text.split(/(\s+)/);

  // Per-unit timing
  const unitDelay = isTypewriter ? 35 : 80; // ms between each char/word

  return (
    <div style={{ position: 'relative' }}>
      {/* Invisible original content — maintains layout and styles */}
      <div ref={containerRef} style={{ visibility: 'hidden', position: 'absolute', pointerEvents: 'none' }}>
        {children}
      </div>

      {/* Animated overlay — inherits same styles from parent positioning */}
      <div aria-hidden="true">
        {text ? (
          <RevealOverlay
            units={units}
            revealed={revealed}
            unitDelay={unitDelay}
            isTypewriter={isTypewriter}
            containerRef={containerRef}
          />
        ) : (
          // Fallback: show children normally before text is extracted
          <div style={{ opacity: visible && !text ? 1 : 0 }}>{children}</div>
        )}
      </div>
    </div>
  );
}

// Clones the style context from the hidden original and renders animated spans
function RevealOverlay({ units, revealed, unitDelay, isTypewriter, containerRef }) {
  // Copy computed styles from the original content
  const [styles, setStyles] = useState({});

  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current.querySelector('div, h1, h2, p, span') || containerRef.current;
    const computed = window.getComputedStyle(el);
    setStyles({
      fontFamily: computed.fontFamily,
      fontSize: computed.fontSize,
      color: computed.color,
      textAlign: computed.textAlign,
      lineHeight: computed.lineHeight,
      letterSpacing: computed.letterSpacing,
      textTransform: computed.textTransform,
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      overflowWrap: 'break-word',
    });
  }, [containerRef]);

  return (
    <div style={styles}>
      {units.map((unit, i) => {
        // Whitespace — render as-is
        if (/^\s+$/.test(unit)) {
          return <span key={i}>{unit}</span>;
        }

        const d = i * unitDelay;

        return (
          <span
            key={i}
            style={{
              display: 'inline',
              opacity: revealed ? 1 : 0,
              transform: revealed ? 'translateY(0)' : 'translateY(4px)',
              transition: `opacity ${isTypewriter ? '0.08s' : '0.35s'} ease ${d}ms, transform 0.3s ease ${d}ms`,
            }}
          >
            {unit}
          </span>
        );
      })}
    </div>
  );
}

export { ANIMATIONS };
